import React, { useState, useRef } from 'react';
import axios from 'axios';

const MedicalHistoryModal = ({
    isOpen,
    onClose,
    medicalHistory,
    setMedicalHistory,
    medication,
    setMedication,
    // EMR 진료 데이터로 서버가 만든 요약. { history, medication, syncedAt }
    emrSummary,
    onSave
}) => {
    const localFileInputRef = useRef(null);
    const [isOcrProcessing, setIsOcrProcessing] = useState(false);
    const [ocrTarget, setOcrTarget] = useState(null);

    const handleOcrClick = (target) => {
        setOcrTarget(target);
        if (localFileInputRef.current) {
            localFileInputRef.current.click();
        }
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setIsOcrProcessing(true);
        
        const reader = new FileReader();
        reader.onloadend = async () => {
            try {
                const base64data = reader.result.split(',')[1];
                const token = localStorage.getItem('token');
                
                const response = await axios.post('/api/ocr', {
                    fileBase64: base64data,
                    fileName: file.name,
                    fileFormat: file.name.split('.').pop().toLowerCase()
                }, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });

                // NCP OCR 응답 파싱
                let text = '';
                if (response.data && response.data.images && response.data.images[0].inferResult === 'SUCCESS') {
                    const fields = response.data.images[0].fields;
                    text = fields.map(field => field.inferText).join(' ');
                }

                if (text) {
                    if (ocrTarget === 'history') {
                        setMedicalHistory(prev => prev + (prev ? '\n' : '') + text);
                    } else {
                        setMedication(prev => prev + (prev ? '\n' : '') + text);
                    }
                } else {
                    alert('이미지에서 텍스트를 인식하지 못했습니다.');
                }

            } catch (error) {
                console.error('OCR 처리 오류:', error);
                alert('이미지 인식 중 오류가 발생했습니다.');
            } finally {
                setIsOcrProcessing(false);
                if (localFileInputRef.current) localFileInputRef.current.value = '';
            }
        };
        reader.readAsDataURL(file);
    };

    const summary = emrSummary || { history: '', medication: '', syncedAt: null };

    // "언제 기준 요약인가". EMR 이 최근 3회를 반복 전송하므로 내용이 같아도 시각은 갱신된다.
    const syncedLabel = (() => {
        if (!summary.syncedAt) return null;
        const d = new Date(summary.syncedAt);
        if (Number.isNaN(d.getTime())) return null;
        const p = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
    })();

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content umd-memo-modal" onClick={e => e.stopPropagation()} style={{ width: '600px', height: '90vh', display: 'flex', flexDirection: 'column' }}>
                <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
                    <h2 style={{ margin: 0, fontSize: '18px' }}>병력 및 약물 입력</h2>
                    <button className="close-btn" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}>&times;</button>
                </div>
                <div className="modal-body" style={{ padding: '20px 0', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px', overflowY: 'auto' }}>
                    <input 
                        type="file" 
                        accept="image/*" 
                        capture="environment" 
                        ref={localFileInputRef} 
                        style={{ display: 'none' }} 
                        onChange={handleFileChange} 
                    />
                    
                    <div className="form-group" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <label><strong>병력 정보</strong> <span style={{ fontWeight: 400, color: '#888', fontSize: '12px' }}>· 직원 작성</span></label>
                            <button 
                                className="send-alert-button" 
                                onClick={() => handleOcrClick('history')}
                                disabled={isOcrProcessing}
                                style={{ width: 'auto', minWidth: '160px', padding: '6px 12px', fontSize: '13px', margin: 0, borderRadius: '6px' }}
                            >
                                <span>📷</span> 캡쳐/이미지 텍스트 추출
                            </button>
                        </div>
                        <EmrSummaryBlock
                            text={summary.history}
                            syncedLabel={syncedLabel}
                            onCopy={() => setMedicalHistory(prev => (prev ? prev + '\n' : '') + summary.history)}
                        />
                        <textarea
                            value={medicalHistory}
                            onChange={(e) => setMedicalHistory(e.target.value)}
                            placeholder="병력 정보를 입력하세요."
                            style={{ flex: 1, width: '100%', minHeight: '90px', padding: '10px', borderRadius: '4px', border: '1px solid #ddd', resize: 'none' }}
                        />
                    </div>

                    <div className="form-group" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <label><strong>약물 정보</strong> <span style={{ fontWeight: 400, color: '#888', fontSize: '12px' }}>· 직원 작성</span></label>
                            <button 
                                className="send-alert-button" 
                                onClick={() => handleOcrClick('medication')}
                                disabled={isOcrProcessing}
                                style={{ width: 'auto', minWidth: '160px', padding: '6px 12px', fontSize: '13px', margin: 0, borderRadius: '6px' }}
                            >
                                <span>📷</span> 캡쳐/이미지 텍스트 추출
                            </button>
                        </div>
                        <EmrSummaryBlock
                            text={summary.medication}
                            syncedLabel={syncedLabel}
                            onCopy={() => setMedication(prev => (prev ? prev + '\n' : '') + summary.medication)}
                        />
                        <textarea
                            value={medication}
                            onChange={(e) => setMedication(e.target.value)}
                            placeholder="약물 정보를 입력하세요."
                            style={{ flex: 1, width: '100%', minHeight: '90px', padding: '10px', borderRadius: '4px', border: '1px solid #ddd', resize: 'none' }}
                        />
                    </div>
                    
                    {isOcrProcessing && (
                        <div style={{ textAlign: 'center', marginTop: '10px', color: '#666' }}>
                            <small>이미지에서 텍스트를 추출하는 중입니다... 잠시만 기다려주세요.</small>
                        </div>
                    )}
                </div>
                <div className="modal-footer" style={{ display: 'flex', justifyContent: 'center', gap: '12px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
                    <button className="submit-btn" onClick={onSave} style={{ padding: '8px 30px', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '15px', fontWeight: 'bold' }}>저장</button>
                    <button className="cancel-btn" onClick={onClose} style={{ padding: '8px 30px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '15px', fontWeight: 'bold' }}>취소</button>
                </div>
            </div>
        </div>
    );
};

/**
 * EMR 진료 데이터로 서버가 만든 요약. **읽기 전용**이다.
 *
 * 직원이 쓰는 칸과 한눈에 갈려야 한다 — 이 블록은 다음 EMR 수신 때 통째로 다시
 * 만들어지므로 여기서 고쳐도 남지 않는다. 대신 "내 칸에 붙여넣기" 로 옮겨 적으면
 * 그때부터는 직원이 쓴 내용이라 자동 갱신의 영향을 받지 않는다.
 *
 * 요약이 비는 것은 정상이다. 예를 들어 4일치 감기약만 받은 회차는 "복용 중인 약물" 이
 * 아니라서 약물 요약에 담지 않는다.
 */
const EmrSummaryBlock = ({ text, syncedLabel, onCopy }) => {
    if (!text) return null;
    return (
        <div style={{ marginBottom: '8px', border: '1px solid #d6e4f0', borderRadius: '6px', background: '#f5f9fd' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', borderBottom: '1px solid #e5eef7' }}>
                <span style={{ fontSize: '12px', color: '#2a6496', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    EMR 자동 요약{syncedLabel ? ` · ${syncedLabel}` : ''}
                </span>
                <button
                    onClick={onCopy}
                    // width:auto·flex 고정이 없으면 전역 버튼 규칙에 눌려 가로로 늘어난다
                    // (OCR 버튼도 같은 이유로 width:auto 를 준다). 늘어나면 왼쪽 제목이 두 줄로 접힌다.
                    style={{ flex: '0 0 auto', width: 'auto', margin: 0, background: 'none', border: '1px solid #2a6496', color: '#2a6496', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                    내 칸에 붙여넣기
                </button>
            </div>
            <pre style={{ margin: 0, padding: '8px 10px', fontSize: '13px', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', color: '#333' }}>
                {text}
            </pre>
        </div>
    );
};

export default MedicalHistoryModal;
